import DOMPurify from 'dompurify';
import { defineCustomElement } from 'vue';

type VueCustomElement = Parameters<typeof defineCustomElement>[0];

/**
 * Wait for an Element to be added to the DOM.
 *
 * Checks for the element. If not found, will wait using MutationObserver.
 *
 * @param selector CSS selector
 * @returns Promise of Element, when it has been added to the DOM.
 */
export async function waitForElement<T extends HTMLElement>(selector: string) {
  return new Promise<T | null>(resolve => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector) as T);
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        return resolve(document.querySelector(selector) as T);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

/**
 * Split URL path into segments and add each segment to the <body> classList.
 *
 * Useful for identifying pages in CSS based on their URL.
 */
export function appendPathAsClassname(pathname = window.location.pathname, previous?: string) {
  const getClasses = (value: string) =>
    value
      .split('/')
      .filter(Boolean)
      .map(segment => segment.replace('.html', ''));

  if (previous) {
    const removedClassnames = getClasses(previous);
    document.body.classList.remove(...removedClassnames);
  }

  const addedClassnames = getClasses(pathname);
  document.body.classList.add(...addedClassnames);
}

/**
 * Define custom Vue elements and register with the custom elements registry.
 *
 * @example
 * defineCustomElementVue({
 *   'mmm-element': MmmElementCe,
 *   'mmm-other-element': MmmOtherElementCe,
 * });
 *
 * @example
 * defineCustomElementVue({
 *   'mmm-element': [MmmElementCe, { extends: 'button' }],
 * });
 *
 * @param elements A vue custom element or element:options tuple
 */
export function defineCustomElementVue(
  elements: Record<
    `${string}-${string}`,
    VueCustomElement | [VueCustomElement, ElementDefinitionOptions]
  >
) {
  Object.entries(elements).forEach(([tag, VueElement]) => {
    if (Array.isArray(VueElement)) {
      const [ElementConstructor, options] = VueElement;
      const Element = defineCustomElement(ElementConstructor);
      return customElements.define(tag, Element, options);
    }

    const Element = defineCustomElement(VueElement);
    return customElements.define(tag, Element);
  });
}

/**
 * Sanitize HTML string with dompurify.
 * @param html HTML string
 * @param config dompurify config
 */
export const sanitizeHtml = (html: string, config: DOMPurify.Config = {}): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    ...config,
  });
};

/**
 * Parse a string as encoded html via generally safe DOMParser API.
 * @param text HTML-encoded text
 * @returns parsed HTML
 */
export const parseHtml = (text: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent;
};
